<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { MenuItem } from '@/types/menu';
import MenuItemActionsMenu from './MenuItemActionsMenu.vue';
import MenuItemPriceDisplay from './MenuItemPriceDisplay.vue';
import MenuItemStatusBadge from './MenuItemStatusBadge.vue';

interface Props {
  menuItems: MenuItem[];
  searchQuery: string;
  canManageItems: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  editItem: [item: MenuItem];
  deleteItem: [item: MenuItem];
  toggleAvailability: [item: MenuItem];
}>();

const { t } = useI18n();

function getImageUrl(item: MenuItem): string | undefined {
  return item.imageUrl || item.image_url;
}

function getItemCategoryName(item: MenuItem): string | null {
  if (!item.category) {
    return null;
  }

  return typeof item.category === 'string' ? item.category : item.category.name;
}
</script>

<template>
  <div class="hidden md:block overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg dark:bg-gray-950">
    <table class="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
      <thead class="bg-gray-50 dark:bg-gray-900">
        <tr>
          <th
            scope="col"
            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 sm:pl-6"
          >
            {{ t('app.views.menu.list.table.name') }}
          </th>
          <th
            scope="col"
            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
          >
            {{ t('app.views.menu.list.table.category') }}
          </th>
          <th
            scope="col"
            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
          >
            {{ t('app.views.menu.list.table.price') }}
          </th>
          <th
            scope="col"
            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
          >
            {{ t('app.views.menu.list.table.status') }}
          </th>
          <th
            scope="col"
            class="relative py-3.5 pl-3 pr-4 sm:pr-6"
          >
            <span class="sr-only">{{ t('app.views.menu.list.table.actions') }}</span>
          </th>
        </tr>
      </thead>

      <tbody class="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
        <tr
          v-for="item in menuItems"
          :key="item.id"
          :data-dropdown-container="`menu-item-desktop-${item.id}`"
          class="relative hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        >
          <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 dark:text-gray-100">
            <div class="flex items-center">
              <div
                v-if="getImageUrl(item)"
                class="h-10 w-10 flex-shrink-0"
              >
                <img
                  class="h-10 w-10 rounded-full object-cover"
                  :src="getImageUrl(item)"
                  :alt="item.name"
                >
              </div>
              <div :class="{ 'ml-4': getImageUrl(item) }">
                {{ item.name }}
                <div
                  v-if="item.variants && item.variants.length > 0"
                  class="text-xs text-gray-500"
                >
                  {{ t('app.views.menu.list.variants', { count: item.variants.length }) }}
                </div>
              </div>
            </div>
          </td>

          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
            <span
              v-if="getItemCategoryName(item)"
              class="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-950 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-100 ring-1 ring-inset ring-blue-700/10"
            >
              {{ getItemCategoryName(item) }}
            </span>
          </td>

          <td class="whitespace-nowrap px-3 py-4 text-sm dark:text-gray-300 text-gray-700">
            <MenuItemPriceDisplay :item="item" />
          </td>

          <td class="whitespace-nowrap px-3 py-4 text-sm dark:text-gray-400">
            <MenuItemStatusBadge :item="item" />
          </td>

          <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 dark:text-gray-400">
            <div
              class="flex justify-end"
              @click.stop
            >
              <MenuItemActionsMenu
                :item="item"
                :dropdown-id="`menu-item-desktop-${item.id}`"
                :can-manage="canManageItems"
                @edit="emit('editItem', $event)"
                @toggle-availability="emit('toggleAvailability', $event)"
                @delete="emit('deleteItem', $event)"
              />
            </div>
          </td>
        </tr>

        <tr v-if="menuItems.length === 0">
          <td
            colspan="5"
            class="px-3 py-4 text-sm text-gray-500 text-center"
          >
            {{ searchQuery.trim() ? (t('app.common.no_results') || 'No se encontraron productos') : t('app.views.menu.list.empty') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
